import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { useConfirmationWizard } from '@/features/Quote/hooks/useConfirmationWizard';
import { hasAddOnProducts } from '@/lib/addOns';
import { isFeatureActive } from '@/lib/featureFlags';
import { type FlowGuidance, type FlowTarget, getFlowGuidance } from '@/lib/flowGuidance';
import { isQuotesComplete } from '@/lib/quoteStatus';
import { FeatureFlag } from '@/types/app';

// Mirrors ProgressIndicator's application-step extraction: /aanvraag/:id/:selected/:step
const APPLICATION_STEP_REGEX = /\/aanvraag\/[^/]+\/[^/]+\/(\d+)/;

/**
 * Resolves the single next action for the current route, composing the existing flow
 * state (route, quote readiness, settings confirmation) into one `FlowGuidance` value.
 * Both the canonical guidance line and the inline control highlights consume this, so
 * the whole flow's guidance comes from one source of truth.
 */
export function useFlowGuidance(): FlowGuidance | null {
	const location = useLocation();
	const { appData, config, confirmedSteps } = useAppContext();
	const { getNextGuidanceStep } = useConfirmationWizard();

	// Same readiness signal the header uses to decide a quote exists on the offer page.
	const quotesReady = isQuotesComplete(appData?.status);

	// On the overview step the confirm button only exists when direct application is
	// allowed; otherwise the sole forward action is discussing with an advisor. Read it
	// the same way Summary does (defaulting on while config loads) so guidance can't point
	// at a button Summary has hidden.
	const directApplicationAllowed = isFeatureActive(
		config,
		FeatureFlag.ALLOW_DIRECT_APPLICATION,
		true
	);

	// Same signal ProductSelection uses to decide whether to render any add-on sections, so
	// guidance skips the "choose add-ons" copy exactly when that step has nothing to offer.
	const addOnsAvailable = hasAddOnProducts(appData?.relevant_product_types);

	const applicationStep = useMemo(() => {
		const match = location.pathname.match(APPLICATION_STEP_REGEX);
		return match ? Number.parseInt(match[1], 10) : null;
	}, [location.pathname]);

	return useMemo(
		() =>
			getFlowGuidance({
				pathname: location.pathname,
				quotesReady,
				directApplicationAllowed,
				// The next actionable settings step, skipping ones already confirmed this
				// wizard session — the same "trusted OR confirmed" rule that turns the cards'
				// checkmark green — so the bar never names a step the user just confirmed.
				firstPendingSettingsStep: getNextGuidanceStep(confirmedSteps),
				addOnsAvailable,
				applicationStep
			}),
		[
			location.pathname,
			quotesReady,
			directApplicationAllowed,
			getNextGuidanceStep,
			confirmedSteps,
			addOnsAvailable,
			applicationStep
		]
	);
}

/**
 * Convenience for control components: true when the current flow guidance points at
 * `target`, so a button/field can show the steady inline highlight that matches the
 * canonical line's narrated next step.
 */
export function useIsFlowTarget(target: FlowTarget): boolean {
	return useFlowGuidance()?.target === target;
}

/**
 * The steady ring the inline highlight paints on the one control the flow guidance points
 * at — a single source of truth so every highlighted control (add-ons, review, application
 * next) looks identical and restyles in one place. No animation, so it reads from a single
 * screenshot the way a vision-based agent perceives the page.
 */
const FLOW_HIGHLIGHT_RING = 'ring-2 ring-primary ring-offset-2 ring-offset-background';

/**
 * Class for a control's steady highlight: the ring when the current flow guidance points at
 * `target`, otherwise ''. Pass `enabled: false` to suppress it while the control itself is
 * not actionable (e.g. a disabled button), so we never lure the visitor toward a dead step.
 * Drop the result straight into `cn(...)`.
 */
export function useFlowHighlightClass(target: FlowTarget, enabled = true): string {
	return useIsFlowTarget(target) && enabled ? FLOW_HIGHLIGHT_RING : '';
}
